import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HttpClient} from '@angular/common/http';
import {
  FeeSnapshot,
  FeeSnapshotItem,
  normalizeFeeSnapshotResponse,
  toEffectiveFeeSnapshot,
} from '@gatewee/types/fee';
import {Wallet} from '@gatewee/types/wallet';
import {GenericCrudService} from '@gatewee/services/generic-crud.service';
import {readProfileRaw} from '@gatewee/services';
import {normalizeDigitableLine} from '@gatewee/utils';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {FeesService} from '../../_services/fees/fees.service';
import {BankSlipPaymentFormComponent} from '../../pages/withdrawals/form/bank-slip/bank-slip-payment-form.component';
import {QrScannerPaneComponent} from '../../pages/withdrawals/form/pix/qr-scanner-pane.component';

export type BankSlipShellFlow = 'form' | 'scanner';

@Component({
  selector: 'app-bank-slip-area',
  templateUrl: './bank-slip-area.component.html',
  styleUrls: ['./bank-slip-area.component.css'],
  standalone: false,
})
export class BankSlipAreaComponent implements OnInit {
  @Input() usePageDrawer = true;

  /** Preferred wallet for pay payload; falls back to primary when null. */
  @Input() walletId: number | null = null;

  pageDrawerOpen = false;

  @HostBinding('class.bank-slip-area--page-drawer-host')
  get pageDrawerHostClass(): boolean {
    return this.usePageDrawer;
  }

  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly pageDrawerOpenChange = new EventEmitter<boolean>();

  activeFlow: BankSlipShellFlow = 'form';
  feeSnapshot: FeeSnapshot | null = null;
  resolvedWalletId: number | null = null;
  walletAvailableBalance: number | null = null;

  @ViewChild('paymentForm')
  paymentForm?: BankSlipPaymentFormComponent;

  @ViewChild('scannerRef')
  scanner?: QrScannerPaneComponent;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly feesService = inject(FeesService);
  private readonly walletsService: GenericCrudService<Wallet>;

  constructor(http: HttpClient) {
    this.walletsService = new GenericCrudService<Wallet>(
      '/wallets/recipient',
      http,
    );
  }

  ngOnInit(): void {
    this.loadFeeSnapshot();
    this.loadWallet();
  }

  get drawerTitleKey(): string {
    if (this.activeFlow === 'scanner') {
      return 'features.withdrawals.modalBankSlip.scannerDrawerTitle';
    }
    return (
      this.paymentForm?.currentTitleKey ??
      'features.withdrawals.modalBankSlip.title'
    );
  }

  get effectiveWalletId(): number | null {
    return this.walletId ?? this.resolvedWalletId;
  }

  openPageDrawer(): void {
    if (!this.usePageDrawer) {
      return;
    }
    this.loadWallet();
    this.pageDrawerOpen = true;
  }

  onPageDrawerOpenChange(open: boolean): void {
    this.pageDrawerOpen = open;
    if (!open) {
      this.resetEmbedSession();
    }
    this.pageDrawerOpenChange.emit(open);
  }

  handleShellBack(): void {
    if (this.activeFlow === 'scanner') {
      this.onScannerBack();
      return;
    }
    if (this.paymentForm?.handleBack()) {
      return;
    }
    this.finishClose();
  }

  onFormShellChange(): void {
    this.cdr.detectChanges();
  }

  openScanner(): void {
    this.activeFlow = 'scanner';
    this.cdr.detectChanges();
  }

  onScannerDetected(raw: string): void {
    this.paymentForm?.setDigitableFromScan(normalizeDigitableLine(raw));
    this.scanner?.reset();
    this.activeFlow = 'form';
    this.cdr.detectChanges();
  }

  onScannerBack(): void {
    this.scanner?.reset();
    this.activeFlow = 'form';
    this.cdr.detectChanges();
  }

  onPaymentCompleted(): void {
    this.finishClose();
  }

  resetEmbedSession(): void {
    this.paymentForm?.reset();
    this.scanner?.reset();
    this.activeFlow = 'form';
  }

  private finishClose(): void {
    this.resetEmbedSession();
    if (this.usePageDrawer) {
      this.pageDrawerOpen = false;
      this.pageDrawerOpenChange.emit(false);
    }
    this.closed.emit();
  }

  private loadFeeSnapshot(): void {
    this.feesService
      .findFeeSnapshotRecipient()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(items => normalizeFeeSnapshotResponse(items)),
        catchError(() => this.resolveFeeSnapshotLegacy()),
      )
      .subscribe(snapshot => {
        this.feeSnapshot = snapshot;
      });
  }

  private resolveFeeSnapshotLegacy(): Observable<FeeSnapshot> {
    const recipientId = this.getRecipientIdFromProfile();
    if (recipientId) {
      return this.feesService.findFeeSnapshot(recipientId).pipe(
        map(raw => normalizeFeeSnapshotResponse(raw)),
        catchError(() => this.findDefaultAsSnapshot()),
      );
    }
    return this.findDefaultAsSnapshot();
  }

  private findDefaultAsSnapshot(): Observable<FeeSnapshot> {
    return this.feesService
      .findDefaultFees()
      .pipe(map((fees: FeeSnapshotItem[]) => toEffectiveFeeSnapshot(fees)));
  }

  private getRecipientIdFromProfile(): number | null {
    try {
      const profile = JSON.parse(readProfileRaw()) as {
        recipient?: {id?: number};
      };
      const id = profile?.recipient?.id;
      return typeof id === 'number' && id > 0 ? id : null;
    } catch {
      return null;
    }
  }

  private loadWallet(): void {
    this.walletsService
      .findAll(undefined, true)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(null)),
      )
      .subscribe((response: unknown) => {
        const wallets =
          response === null ? [] : this.normalizeWalletResponse(response);
        const preferred =
          this.walletId !== null
            ? wallets.find(w => w.id === this.walletId)
            : undefined;
        const primary =
          preferred ?? wallets.find(w => w.isPrimary) ?? wallets[0];
        this.resolvedWalletId = primary?.id ?? null;
        this.walletAvailableBalance = this.coerceNumericBalance(
          primary?.availableBalance,
        );
        this.cdr.markForCheck();
      });
  }

  private normalizeWalletResponse(body: unknown): Wallet[] {
    if (Array.isArray(body)) {
      return body as Wallet[];
    }
    if (
      body &&
      typeof body === 'object' &&
      'records' in body &&
      Array.isArray((body as {records: unknown}).records)
    ) {
      return (body as {records: Wallet[]}).records;
    }
    if (
      body &&
      typeof body === 'object' &&
      'availableBalance' in (body as Record<string, unknown>)
    ) {
      return [body as Wallet];
    }
    return [];
  }

  private coerceNumericBalance(value: unknown): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.length) {
        return null;
      }
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }
}
